// externals
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';

// components
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { OfferTypeEnum, ExtendedOfferTypeEnum } from '@makemydeal/dr-platform-types';
import { BreakdownTabsName, BreakdownTabsLabel } from '@makemydeal/dr-shared-types';
import { SlideOut } from '@interstate/components/SlideOut';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import InterstateDealSummary from '../../dealSummary/DealSummary.interstate';
import DealRecapSummary from '../../dealSummary/DealRecap/DealRecapSummary.interstate';

const BreakdownSlideout = () => {
    const showBreakdownSlideout = useSelector(baseDealSelectors.getBreakdownSlideoutStatus);
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isDealRecapTabEnabled = useSelector(featureToggleSelectors.useDealRecapEnabled);
    const activeBreakdownTab = useSelector(baseDealSelectors.getActiveBreakdownTabDetails);
    const dispatch = useDispatch();
    const isTabletSize = useMediaQuery({
        query: `(min-width: ${BreakPoint.PHONE}) and (max-width:'991px')`
    });
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const activeScopeId = activeBreakdownTab?.activeScopeId;
    const activeTab = activeScopeId && activeBreakdownTab ? activeBreakdownTab[activeScopeId] : null;

    const getPanelWidth = () => {
        if (isTabletSize) return '32.857rem';
        if (isLargeSize) return '67.429rem';
        return '100%';
    };

    const getDealTypeTitle = () => {
        switch (currentOfferType) {
            case OfferTypeEnum.CASH:
                return 'Cash';
            case OfferTypeEnum.LEASE:
                return 'Lease';
            case OfferTypeEnum.FINANCE:
                return 'Finance';
            case ExtendedOfferTypeEnum.BALLOON:
                return 'Balloon';

            default:
                return '';
        }
    };

    const shouldShowDealRecap = isDealRecapTabEnabled && activeTab === BreakdownTabsName.DEALRECAP;

    const getHeaderTitle = () => {
        if (!isDealRecapTabEnabled) {
            return `${getDealTypeTitle()} ${BreakdownTabsLabel.BREAKDOWN}`;
        }
        return `${getDealTypeTitle()} ${
            activeTab === BreakdownTabsName.DEALRECAP ? BreakdownTabsLabel.DEALRECAP : BreakdownTabsLabel.BREAKDOWN
        }`;
    };

    return (
        <SlideOut
            data-testid="breakdown-slide-out"
            id="breakdown-slide-out"
            show={showBreakdownSlideout}
            position="right"
            panelWidth={getPanelWidth()}
            onHide={() => {
                dispatch(baseDealActionCreators.setBreakdownSlideoutStatus(!showBreakdownSlideout));
            }}
            header={getHeaderTitle()}
        >
            {shouldShowDealRecap ? <DealRecapSummary showDetailedView={true} /> : <InterstateDealSummary showDetailedView={true} />}
        </SlideOut>
    );
};

export default BreakdownSlideout;
